import { useSessionUser } from "hooks/useSession";
import { useMemo } from "react";
import { ProfileStatus } from "store/types/enum";
import { priceData } from "config";

export interface UseProfileStatsResult {
  purchasedAddonSeats: number;
  purchasedAddonProjects: number;
  profileStatus: ProfileStatus;
  isDowngradeScheduled: boolean;
}

export const useProfileStats = (): UseProfileStatsResult => {
  const { session: profile } = useSessionUser();

  const purchasedAddonSeats = useMemo(() => {
    if (!profile) return 0;
    const currentPlan = priceData.find((item: any) => Number(item.id) === profile.pricing_plan_id);
    const currentPlanSeats = currentPlan?.seat || 1;
    return (profile?.subscription?.seats_purchased || 0) - currentPlanSeats;
  }, [profile]);

  const purchasedAddonProjects = useMemo(() => {
    if (!profile) return 0;
    const currentPlan = priceData.find((item: any) => Number(item.id) === profile.pricing_plan_id);
    const currentPlanProjects = currentPlan?.project || 1;
    return (profile?.subscription?.projects_purchased || 0) - currentPlanProjects;
  }, [profile]);

  const profileStatus = useMemo(() => {
    if (!profile) return ProfileStatus.Active;

    if (profile.is_active) {
      return ProfileStatus.Active;
    }

    const subscription_end_date = profile.subscription?.end_date;
    if (subscription_end_date) {
      const today = new Date();
      const expirationDate = new Date(subscription_end_date);
      if (today >= expirationDate) {
        return ProfileStatus.SubscriptionExpired;
      }
      return ProfileStatus.Deactivated;
    }

    return ProfileStatus.Active;
  }, [profile]);

  const isDowngradeScheduled = useMemo(() => {
    if (!profile) return false;

    const upcomingPlan = priceData.find(
      (item: any) => Number(item.id) === profile.upcoming_pricing_plan?.pricing_plan_id
    );

    return !!(
      upcomingPlan &&
      (upcomingPlan.id !== profile?.pricing_plan_id?.toString() ||
        profile.upcoming_pricing_plan?.interval !== profile.subscription?.interval)
    );
  }, [profile]);

  return {
    purchasedAddonSeats,
    purchasedAddonProjects,
    profileStatus,
    isDowngradeScheduled,
  };
};
